import React, { useEffect, useState } from "react";
import styled from "styled-components";
import COLORS from "../../../../assets/Colors";
import ActionBar from "../../../common/ActionBar";
import { navigate } from "@reach/router";
import _ from "lodash";
import { SecondaryCTAButton } from "../../../common/Buttons";
import { Formik } from "formik";
import SystemService from "../../../../services/SystemService";
import useLoader from "../../../../hooks/useLoader";
import { useSnackbar } from "notistack";
import {
  AddCircleOutline,
  Edit,
  Save,
  Cancel,
  Delete,
} from "@material-ui/icons";
import { CustomInputField } from "../../../common/FormInputs";
import { ipAddressWhiteListValidationSchema } from "../../../../helpers/validationSchema";
import { Typography } from "@material-ui/core";

const ITEMS_PER_PAGE = 10;

const IpAddressWhitelist = () => {
  const [ipItems, setIpItems] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const { setLoader } = useLoader();
  const { enqueueSnackbar } = useSnackbar();
  const [trigger, setTrigger] = useState(true);

  useEffect(() => {
    async function getIpAddressWhiteLists() {
      try {
        setLoader({ state: true, message: "Fetching IP whitelist..." });
        const response = await SystemService.getIpAddressWhiteLists();
        if (response?.IpAddressWhitelistDetails) {
          setIpItems(response.IpAddressWhitelistDetails);
        }
      } catch (err) {
        enqueueSnackbar("Failed to load IP whitelist", { variant: "error" });
      } finally {
        setLoader({ state: false });
        setTrigger(false);
      }
    }
    if (trigger) getIpAddressWhiteLists();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trigger]);

  const onBreadcrumbClick = (selected) => {
    if (_.snakeCase(selected) === "configurations") {
      navigate("/dashboard/configurations");
    }
  };

  // Pagination
  const totalPages = Math.ceil(ipItems.length / ITEMS_PER_PAGE);

  const handlePageChange = (page) => setCurrentPage(page);

  return (
    <OuterContainer>
      <ActionBar
        breadcrumbs={["Ip Address Whitelist"]}
        {...{ onBreadcrumbClick }}
      />

      <Container>
        <Card>
          <Header>
            <Title variant="h6">IP Address Whitelist</Title>
            <SecondaryCTAButton
              onClick={() =>
                setIpItems([
                  ...ipItems,
                  { ipAddress: "", isNew: true, isEditing: true },
                ])
              }
            >
              <AddCircleOutline style={{ fontSize: 18, marginRight: 8 }} />
              Add IP Address
            </SecondaryCTAButton>
          </Header>

          <Formik
            initialValues={{ items: ipItems }}
            enableReinitialize
            validationSchema={ipAddressWhiteListValidationSchema}
            onSubmit={async (values, { setSubmitting }) => {
              try {
                setLoader({ state: true, message: "Saving..." });
                const newItem = values.items.find((item) => item.isNew);
                if (newItem) {
                  const payload = {
                    ipAddressParams: [{ ipAddress: newItem.ipAddress }],
                  };
                  const res = await SystemService.ipAddressWhiteList(payload);
                  enqueueSnackbar(res?.message || "IP added successfully", {
                    variant: "success",
                  });
                  setTrigger(true); // Refresh list
                }
              } catch (err) {
                enqueueSnackbar(err.message || "Failed to add IP", {
                  variant: "error",
                });
              } finally {
                setLoader({ state: false });
                setSubmitting(false);
              }
            }}
          >
            {({ values, setFieldValue, handleSubmit, isValid }) => (
              <form onSubmit={handleSubmit}>
                <TableContainer>
                  <Table>
                    <thead>
                      <tr>
                        <Th style={{ width: "60%" }}>IP Address</Th>
                        <Th style={{ width: "40%" }}>Actions</Th>
                      </tr>
                    </thead>
                    <tbody>
                      {ipItems.length === 0 ? (
                        <tr>
                          <td
                            colSpan="2"
                            style={{
                              textAlign: "center",
                              padding: "40px",
                              color: "#999",
                            }}
                          >
                            No IP addresses whitelisted yet.
                          </td>
                        </tr>
                      ) : (
                        ipItems.map((item, index) => {
                          const isEditing = item.isEditing || item.isNew;

                          return (
                            <Tr key={item.id || `new-${index}`}>
                              <Td>
                                {isEditing ? (
                                  <CustomInputField
                                    name={`items[${index}].ipAddress`}
                                    value={item.ipAddress || ""}
                                    onChange={(e) => {
                                      const newItems = [...ipItems];
                                      newItems[index].ipAddress =
                                        e.target.value;
                                      setIpItems(newItems);
                                    }}
                                    variant="outlined"
                                    placeholder="e.g. 192.168.1.1"
                                    style={{ width: "100%" }}
                                    autoFocus={item.isNew}
                                  />
                                ) : (
                                  <IpText>{item.ipAddress}</IpText>
                                )}
                              </Td>
                              <Td>
                                <ActionButtons>
                                  {isEditing ? (
                                    <>
                                      <IconButton
                                        type="button"
                                        success
                                        onClick={async () => {
                                          if (item.isNew) {
                                            if (!item.ipAddress.trim()) {
                                              enqueueSnackbar(
                                                "IP address is required",
                                                { variant: "warning" }
                                              );
                                              return;
                                            }
                                            try {
                                              setLoader({ state: true });
                                              const payload = {
                                                ipAddressParams: [
                                                  { ipAddress: item.ipAddress },
                                                ],
                                              };
                                              await SystemService.ipAddressWhiteList(
                                                payload
                                              );
                                              enqueueSnackbar(
                                                "IP added successfully",
                                                { variant: "success" }
                                              );
                                              setTrigger(true);
                                            } catch (err) {
                                              enqueueSnackbar(err.message, {
                                                variant: "error",
                                              });
                                            } finally {
                                              setLoader({ state: false });
                                            }
                                          } else {
                                            try {
                                              setLoader({ state: true });
                                              await SystemService.updateIpAddressWhiteLists(
                                                item.id,
                                                { ipAddress: item.ipAddress }
                                              );
                                              enqueueSnackbar(
                                                "Updated successfully",
                                                { variant: "success" }
                                              );
                                              const updated = [...ipItems];
                                              updated[index].isEditing = false;
                                              setIpItems(updated);
                                            } catch (err) {
                                              enqueueSnackbar(err.message, {
                                                variant: "error",
                                              });
                                            } finally {
                                              setLoader({ state: false });
                                            }
                                          }
                                        }}
                                      >
                                        <Save fontSize="small" />
                                      </IconButton>

                                      <IconButton
                                        danger
                                        onClick={() => {
                                          if (item.isNew) {
                                            setIpItems(
                                              ipItems.filter(
                                                (_, i) => i !== index
                                              )
                                            );
                                          } else {
                                            const updated = [...ipItems];
                                            updated[index].isEditing = false;
                                            setIpItems(updated);
                                          }
                                        }}
                                      >
                                        <Cancel
                                          style={{ fill: "#d91b1b" }}
                                          fontSize="small"
                                        />
                                      </IconButton>
                                    </>
                                  ) : (
                                    <>
                                      <IconButton
                                        onClick={() => {
                                          const updated = [...ipItems];
                                          updated[index].isEditing = true;
                                          setIpItems(updated);
                                        }}
                                      >
                                        <Edit fontSize="small" />
                                      </IconButton>

                                      <IconButton
                                        danger
                                        onClick={async () => {
                                          try {
                                            setLoader({ state: true });
                                            await SystemService.deleteIpAddressWhiteLists(
                                              item.id
                                            );
                                            enqueueSnackbar(
                                              "Deleted successfully",
                                              { variant: "success" }
                                            );
                                            setTrigger(true);
                                          } catch (err) {
                                            enqueueSnackbar(err.message, {
                                              variant: "error",
                                            });
                                          } finally {
                                            setLoader({ state: false });
                                          }
                                        }}
                                      >
                                        <Delete
                                          style={{ fill: "#d91b1b" }}
                                          fontSize="small"
                                        />
                                      </IconButton>
                                    </>
                                  )}
                                </ActionButtons>
                              </Td>
                            </Tr>
                          );
                        })
                      )}
                    </tbody>
                  </Table>
                </TableContainer>

                {totalPages > 1 && (
                  <PaginationWrapper>
                    <PaginationButtons>
                      <PageButton
                        onClick={() =>
                          handlePageChange(Math.max(1, currentPage - 1))
                        }
                        disabled={currentPage === 1}
                      >
                        Previous
                      </PageButton>
                      {[...Array(totalPages)].map((_, i) => (
                        <PageNumber
                          key={i + 1}
                          active={currentPage === i + 1}
                          onClick={() => handlePageChange(i + 1)}
                        >
                          {i + 1}
                        </PageNumber>
                      ))}
                      <PageButton
                        onClick={() =>
                          handlePageChange(
                            Math.min(totalPages, currentPage + 1)
                          )
                        }
                        disabled={currentPage === totalPages}
                      >
                        Next
                      </PageButton>
                    </PaginationButtons>
                  </PaginationWrapper>
                )}
              </form>
            )}
          </Formik>
        </Card>
      </Container>
    </OuterContainer>
  );
};

// Styled Components
const OuterContainer = styled.div`
  background-color: ${COLORS.PRIMARY_WHITE};
  min-height: 100vh;
  padding: 20px 15px;
`;

const Container = styled.div`
  max-width: 1000px;
  margin: 0 auto;
`;

const Card = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  overflow: hidden;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  background-color: #f8f9fa;
  border-bottom: 1px solid #eee;
`;

const TableContainer = styled.div`
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Th = styled.th`
  text-align: left;
  padding: 16px 20px;
  background-color: #f1f5f9;
  font-family: openSans-SemiBold, sans-serif;
  color: #444;
  font-size: 14px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const Tr = styled.tr`
  border-bottom: 1px solid #eee;
  &:last-child {
    border-bottom: none;
  }
  &:hover {
    background-color: #f9fafa;
  }
`;

const Td = styled.td`
  padding: 16px 20px;
  vertical-align: middle;
`;

const IpText = styled.div`
  font-family: openSans-SemiBold, sans-serif;
  font-size: 15px;
  color: #293461;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 8px;
`;

const Title = styled(Typography)`
  font-family: openSans-SemiBold, sans-serif;
  color: #333;
`;

const IconButton = styled.button`
  width: 36px;
  height: 36px;
  border-radius: 8px;
  border: none;
  background-color: ${(props) =>
    props.success ? "#d4edda" : props.danger ? "#f8d7da" : "#e2e8f5"};
  color: ${(props) =>
    props.success ? "#155724" : props.danger ? "#721c24" : "#3b82f6"};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

// Pagination Styles
const PaginationWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  background-color: #f8f9fa;
  border-top: 1px solid #eee;
`;

const PaginationButtons = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const PageButton = styled.button`
  padding: 8px 16px;
  border: 1px solid #ddd;
  background: white;
  color: #333;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &:hover:not(:disabled) {
    background-color: #f0f0f0;
  }
`;

const PageNumber = styled.button`
  width: 36px;
  height: 36px;
  border-radius: 6px;
  border: none;
  background-color: ${(props) => (props.active ? COLORS.BTN_GREEN : "white")};
  color: ${(props) => (props.active ? "white" : "#333")};
  font-weight: ${(props) => (props.active ? "bold" : "normal")};
  cursor: pointer;

  &:hover {
    background-color: ${(props) =>
      props.active ? COLORS.BTN_GREEN : "#f0f0f0"};
  }
`;

export default IpAddressWhitelist;
